import { useMutation, useQueryClient } from '@tanstack/react-query'
import { reorderAgenticPrompts } from 'services/agenticPrompts'
import { AGENTIC_PROMPTS_QUERY_KEY } from 'pages/AgenticPrompts/hooks/useAgenticPrompts'

/**
 * Reassigns position values for the reordered items using the slots they currently occupy.
 *
 * Why we reuse existing slots instead of generating 1..N indices:
 * Prompts have global positions across the entire database, but the grid may be displaying
 * a filtered subset (e.g. filtered by workflow). If we reassigned positions as 1, 2, 3...,
 * we would overwrite and collide with prompts outside the current filter.
 *
 * How it works:
 * 1. Collect all position values currently held by the visible items and sort them ascending.
 * 2. Pair each ID in the newly dropped order with the corresponding slot position.
 * 3. Update the visible items with their new positions while leaving all other items untouched.
 *
 * Example:
 * If items [A (pos 10), B (pos 20), C (pos 30)] are reordered to [C, A, B]:
 * - Available slots: [10, 20, 30]
 * - Resulting positions: C -> 10, A -> 20, B -> 30
 *
 * @param {Array<{id: string|number, position: number}>} allItems - All items currently in query cache
 * @param {Array<string|number>} reorderedVisibleIds - IDs of the visible rows in their new dropped order
 * @returns {Array<{id: string|number, position: number}>} Items with updated positions
 */
export function reassignVisibleSlots(allItems, reorderedVisibleIds) {
  const orderedIds = reorderedVisibleIds.map(String)
  const reorderedIdSet = new Set(orderedIds)

  // Step 1: Extract and sort the position slots currently held by the reordered items
  const availableSlots = allItems
    .filter(item => reorderedIdSet.has(String(item.id)))
    .map(item => item.position)
    .sort((a, b) => a - b)

  // Step 2: Map each ID in the new order to the corresponding sorted slot
  const newPositionById = new Map(orderedIds.map((id, index) => [id, availableSlots[index]]))

  // Step 3: Apply the new positions back to the items list
  return allItems.map(item => {
    const newPosition = newPositionById.get(String(item.id))

    return newPosition === undefined ? item : { ...item, position: newPosition }
  })
}

/**
 * Hook to persist drag-and-drop prompt reordering with optimistic updates.
 *
 * Workflow:
 * 1. `onMutate`: Optimistically update the query cache with the new positions and sort order
 *    so the UI updates instantly without snapping back. Save previous cache state for rollback.
 * 2. `mutationFn`: Extract the newly assigned positions from the cache and send them to the API.
 * 3. `onError`: Roll back to the previous cache state if the API request fails.
 * 4. `onSettled`: Invalidate queries to ensure frontend state matches the backend.
 *
 * @param {object} apiFilters - Active filters for the prompt list query key (so cache updates target the exact rendered view)
 * @returns {object} React Query mutation object; call `mutate({ visibleRowIds })` with row IDs in the new order
 */
export function useReorderAgenticPrompts(apiFilters) {
  const queryClient = useQueryClient()
  const queryKey = [AGENTIC_PROMPTS_QUERY_KEY, apiFilters]

  return useMutation({
    mutationFn: async ({ visibleRowIds }) => {
      const visibleIdSet = new Set(visibleRowIds.map(String))

      // Read the newly assigned positions from the cache (set during onMutate)
      const cachedPrompts = queryClient.getQueryData(queryKey)?.data ?? []
      const promptPositions = cachedPrompts
        .filter(prompt => visibleIdSet.has(String(prompt.id)))
        .map(({ id, position }) => ({ id, position }))
        .filter(({ position }) => Number.isFinite(position))

      if (promptPositions.length === 0) {
        return
      }

      await reorderAgenticPrompts(promptPositions)
    },

    onMutate: async ({ visibleRowIds }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey })

      const previousData = queryClient.getQueryData(queryKey)

      // Optimistically update cache: reassign slots and re-sort array by position
      queryClient.setQueryData(queryKey, oldData => {
        if (!oldData) {
          return oldData
        }

        const updatedData = reassignVisibleSlots(oldData.data ?? [], visibleRowIds)
        const sortedData = updatedData.sort((a, b) => a.position - b.position)

        return {
          ...oldData,
          data: sortedData,
        }
      })

      // Return context with previous data for rollback in onError
      return { previousData }
    },

    onError: (error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })
}
